'use client';
import React, { useEffect, useRef, useState } from 'react';

interface Package {
  id: string;
  name: string;
  price: number;
  total_hours: number;
  description: string | null;
  is_active: boolean;
}

interface PriceItem {
  label: string;
  price: string;
}

interface Props {
  packages: Package[];
  priceList: PriceItem[];
}

function useReveal(delay = 0) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => setVisible(true), delay);
          obs.unobserve(el);
        }
      },
      { threshold: 0.08, rootMargin: '0px 0px -40px 0px' }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [delay]);
  return { ref, visible };
}

function formatPrice(price: number): string {
  return `€ ${price.toLocaleString('nl-NL', { minimumFractionDigits: 0, maximumFractionDigits: 2 })},-`;
}

function getPackageFeatures(pkg: Package): string[] {
  const features = ['Praktijkexamen', 'Flexibele rijtijden'];
  if (pkg.total_hours >= 40) {
    features.splice(1, 0, 'Theorie Cursus');
  }
  return features;
}

export default function TarievenContent({ packages, priceList }: Props) {
  const hero = useReveal(0);
  const pkgHeading = useReveal(0);
  const pkg0 = useReveal(0);
  const pkg1 = useReveal(150);
  const pkg2 = useReveal(300);
  const priceHeading = useReveal(0);
  const priceTable = useReveal(100);
  const cta = useReveal(0);

  const pkgRefs = [pkg0, pkg1, pkg2];

  return (
    <>
      {/* ── HERO ── */}
      <section className="bg-gray-950 overflow-hidden relative">
        {/* Accent bar */}
        <div className="absolute left-0 top-0 bottom-0 w-1 bg-brand" />

        <div
          ref={hero.ref}
          className="max-w-screen-xl mx-auto px-6 md:px-16 py-20 md:py-32 transition-all duration-700 ease-out"
          style={{ opacity: hero.visible ? 1 : 0, transform: hero.visible ? 'translateY(0)' : 'translateY(48px)' }}
        >
          {/* Breadcrumb */}
          <div className="flex items-center text-sm space-x-1.5 text-gray-600 mb-8">
            <a href="/" aria-label="Homepage" className="hover:text-gray-300 transition-colors">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                <path fillRule="evenodd" d="M9.293 2.293a1 1 0 011.414 0l7 7A1 1 0 0117 11h-1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-3a1 1 0 00-1-1H9a1 1 0 00-1 1v3a1 1 0 01-1 1H5a1 1 0 01-1-1v-6H3a1 1 0 01-.707-1.707l7-7z" clipRule="evenodd" />
              </svg>
            </a>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-3 h-3 opacity-40 text-gray-500">
              <path fillRule="evenodd" d="M16.28 11.47a.75.75 0 010 1.06l-7.5 7.5a.75.75 0 01-1.06-1.06L14.69 12 7.72 5.03a.75.75 0 011.06-1.06l7.5 7.5z" clipRule="evenodd" />
            </svg>
            <span className="text-gray-600">Tarieven</span>
          </div>

          <div className="flex items-center gap-4 mb-6">
            <span className="w-10 h-px bg-brand" />
            <span className="text-brand text-xs font-bold uppercase tracking-[0.25em]">Transparante prijzen</span>
          </div>

          <h1 className="font-black text-white leading-none mb-6" style={{ fontSize: 'clamp(3rem, 8vw, 7rem)' }}>
            Kies jouw<br />
            <span style={{ color: 'transparent', WebkitTextStroke: '2px #D4AF37' }}>pakket</span>
          </h1>
          <p className="text-gray-400 text-lg max-w-lg leading-relaxed">
            Eerlijke tarieven zonder verrassingen. Betalen in termijnen is altijd mogelijk.
          </p>
        </div>
      </section>

      {/* ── PACKAGES ── */}
      <section className="bg-white py-20 md:py-32 px-6 md:px-16">
        <div className="max-w-screen-xl mx-auto">
          <div
            ref={pkgHeading.ref}
            className="mb-12 transition-all duration-700 ease-out"
            style={{ opacity: pkgHeading.visible ? 1 : 0, transform: pkgHeading.visible ? 'translateY(0)' : 'translateY(32px)' }}
          >
            <div className="flex items-center gap-4 mb-4">
              <span className="w-10 h-px bg-brand" />
              <span className="text-brand text-xs font-bold uppercase tracking-[0.25em]">Lespakketten</span>
            </div>
            <h2 className="font-black text-gray-900 text-4xl md:text-5xl leading-none">Alles inbegrepen</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-0 border border-gray-100">
            {packages?.map((pkg, index) => {
              const isFeatured = index === 1;
              const features = getPackageFeatures(pkg as Package);
              const r = pkgRefs[index] || pkg0;
              return (
                <div
                  key={pkg?.id}
                  ref={r.ref}
                  className={`relative flex flex-col p-10 md:p-12 border-r last:border-r-0 border-gray-100 transition-all duration-700 ease-out ${
                    isFeatured ? 'bg-gray-950 text-white' : 'bg-white text-gray-900'
                  }`}
                  style={{
                    opacity: r.visible ? 1 : 0,
                    transform: r.visible ? 'translateY(0)' : 'translateY(60px)',
                    transitionDelay: `${index * 0.15}s`,
                  }}
                >
                  {isFeatured && (
                    <div className="absolute top-0 left-0 right-0 h-1 bg-brand" />
                  )}
                  {isFeatured && (
                    <div className="inline-block bg-brand text-white text-xs font-bold uppercase tracking-widest px-3 py-1 mb-6 self-start">
                      Meest gekozen
                    </div>
                  )}

                  <div className={`text-xs font-bold uppercase tracking-[0.2em] mb-2 ${isFeatured ? 'text-brand' : 'text-gray-400'}`}>
                    {pkg?.total_hours} uur
                  </div>
                  <div className="font-black text-2xl mb-8">{pkg?.name}</div>

                  <div className="space-y-3 flex-1 mb-10">
                    {features?.map((feature, i) => (
                      <div key={i} className="flex items-center gap-3 text-sm">
                        <div className={`w-4 h-4 flex items-center justify-center shrink-0 ${isFeatured ? 'text-brand' : 'text-gray-400'}`}>
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                            <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <span className={isFeatured ? 'text-gray-300' : 'text-gray-600'}>{feature}</span>
                      </div>
                    ))}
                  </div>

                  <div className={`pt-8 border-t ${isFeatured ? 'border-white/10' : 'border-gray-100'}`}>
                    <div className="font-black text-4xl mb-6">{formatPrice(Number(pkg?.price))}</div>
                    <a
                      href="https://driveplannen.nl/registreer/46ec6865-5dc5-4e1c-a646-ce961abc1633"
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`w-full block text-center font-bold py-4 text-sm uppercase tracking-widest transition-all active:scale-95 duration-150 ${
                        isFeatured
                          ? 'bg-brand text-white hover:bg-brand/90' :'bg-gray-950 text-white hover:bg-gray-800'
                      }`}
                    >
                      Inschrijven
                    </a>
                  </div>
                </div>
              );
            })}
          </div>

          <p className="text-center text-gray-400 text-sm mt-6">
            Betalen in termijnen is mogelijk. Neem contact op voor meer informatie.
          </p>
        </div>
      </section>

      {/* ── PRICE TABLE ── */}
      <section className="bg-gray-50 py-20 md:py-32 px-6 md:px-16">
        <div className="max-w-screen-xl mx-auto grid md:grid-cols-2 gap-16 items-start">
          <div
            ref={priceHeading.ref}
            className="transition-all duration-700 ease-out"
            style={{ opacity: priceHeading.visible ? 1 : 0, transform: priceHeading.visible ? 'translateX(0)' : 'translateX(-48px)' }}
          >
            <div className="flex items-center gap-4 mb-6">
              <span className="w-10 h-px bg-brand" />
              <span className="text-brand text-xs font-bold uppercase tracking-[0.25em]">Losse tarieven</span>
            </div>
            <h2 className="font-black text-gray-900 leading-none mb-6" style={{ fontSize: 'clamp(2.5rem, 5vw, 4.5rem)' }}>
              Prijslijst
            </h2>
            <p className="text-gray-500 leading-relaxed max-w-sm">
              Geen verborgen kosten. Alles wat je nodig hebt voor je rijbewijs, overzichtelijk op een rij.
            </p>
          </div>

          <div
            ref={priceTable.ref}
            className="transition-all duration-700 ease-out"
            style={{ opacity: priceTable.visible ? 1 : 0, transform: priceTable.visible ? 'translateX(0)' : 'translateX(48px)' }}
          >
            <div className="border border-gray-200 overflow-hidden">
              {priceList?.map((item, i) => (
                <div
                  key={item?.label}
                  className={`flex items-center justify-between px-8 py-5 ${
                    i !== priceList.length - 1 ? 'border-b border-gray-100' : ''
                  } ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
                >
                  <span className="font-semibold text-gray-800">{item?.label}</span>
                  <span className="text-gray-900 font-black text-xl">{item?.price}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA BANNER ── */}
      <section
        ref={cta.ref}
        className="bg-brand py-20 md:py-28 px-6 md:px-16 transition-all duration-700 ease-out"
        style={{ opacity: cta.visible ? 1 : 0, transform: cta.visible ? 'translateY(0)' : 'translateY(48px)' }}
      >
        <div className="max-w-screen-xl mx-auto flex flex-col md:flex-row md:items-center md:justify-between gap-8">
          <div>
            <h2 className="font-black text-white leading-none mb-3" style={{ fontSize: 'clamp(2rem, 5vw, 4rem)' }}>
              Nog vragen?
            </h2>
            <p className="text-white/80 text-lg max-w-md">We helpen je graag verder. Stuur een bericht of bel ons direct.</p>
          </div>
          <a
            href="/contact"
            className="inline-flex items-center gap-3 bg-white text-gray-950 font-black px-8 py-5 text-sm uppercase tracking-widest hover:bg-gray-100 active:scale-95 transition-all duration-150 shrink-0"
          >
            Neem contact op
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
              <path fillRule="evenodd" d="M3 10a.75.75 0 01.75-.75h10.638L10.23 5.29a.75.75 0 111.04-1.08l5.5 5.25a.75.75 0 010 1.08l-5.5 5.25a.75.75 0 11-1.04-1.08l4.158-3.96H3.75A.75.75 0 013 10z" clipRule="evenodd" />
            </svg>
          </a>
        </div>
      </section>
    </>
  );
}
